/**
 * Analytics Constants
 * Centralized definitions for feature names, page names, and event types
 */

// Feature names for tracking (sorted alphabetically)
export const FEATURES = {
  CCM: 'CCM',
  DCN_AUDIT: 'DCNAudit',
  GENERATE_PRICE_DCN: 'GeneratePriceDCN',
  GENERATE_PRODUCT_DCN: 'GenerateProductDCN',
  O2O_CAPABILITIES: 'O2O_Capabilities',
  O2O_ORDER_LOOKUP: 'O2O_OrderLookup',
  PATCH: 'Patch',
  POS_REASONS: 'POSReasons',
  PRINTER_PORTAL: 'PrinterPortal',
  PRODUCT_ANALYZER: 'ProductAnalyzer',
  REGISTER_LEVEL_TRACKING: 'RegisterLevelTracking',
  SCS: 'SCS',
  SCS_BULK_EDIT: 'SCS_BulkEdit',
  SCS_CONFIG_BY_KEY: 'SCS_ConfigByKey',
  SCS_SCHEDULED_CHANGES: 'SCS_ScheduledChanges',
  SLS: 'SLS',
  SLS_DIGITAL_STORES: 'SLS_DigitalStores',
  SLS_STORES: 'SLS_Stores',
  SLS_WAREHOUSES: 'SLS_Warehouses',
  SNI_TRANSACTION_SEARCH: 'SNITransactionSearch',
  STORE_HIERARCHY: 'StoreHierarchy',
  STORE_HIERARCHY_SCHEDULE: 'StoreHierarchySchedule',
  STORE_SERVICES: 'StoreServices',
  TAX_IDENTIFIERS: 'TaxIdentifiers',
  TRAFFIC: 'Traffic',
  TRANSACTION_ANALYZER: 'TransactionAnalyzer',
  WAITLINES: 'Waitlines',
  ZONES: 'Zones',
};

// Page paths mapped to feature names (sorted alphabetically)
export const PAGE_TO_FEATURE = {
  '/commonconfigmanager/config': FEATURES.CCM,
  '/dcnaudit': FEATURES.DCN_AUDIT,
  '/generatepricedcn': FEATURES.GENERATE_PRICE_DCN,
  '/generateproductdcn': FEATURES.GENERATE_PRODUCT_DCN,
  '/onlinetooffline': FEATURES.O2O_CAPABILITIES,
  '/onlinetooffline/orderlookup': FEATURES.O2O_ORDER_LOOKUP,
  '/patch': FEATURES.PATCH,
  '/posreasons': FEATURES.POS_REASONS,
  '/printerportal': FEATURES.PRINTER_PORTAL,
  '/productanalyzer': FEATURES.PRODUCT_ANALYZER,
  '/registerleveltracking': FEATURES.REGISTER_LEVEL_TRACKING,
  '/snitransactionsearch': FEATURES.SNI_TRANSACTION_SEARCH,
  '/storeconfigservice/bulkedit': FEATURES.SCS_BULK_EDIT,
  '/storeconfigservice/managestoreconfig': FEATURES.SCS,
  '/storeconfigservice/scheduledchanges': FEATURES.SCS_SCHEDULED_CHANGES,
  '/storeconfigservice/searchconfigbykey': FEATURES.SCS_CONFIG_BY_KEY,
  '/storehierarchy': FEATURES.STORE_HIERARCHY,
  '/storehierarchy/schedule': FEATURES.STORE_HIERARCHY_SCHEDULE,
  '/storelocationservices': FEATURES.SLS,
  '/storeservices': FEATURES.STORE_SERVICES,
  '/taxidentifiers': FEATURES.TAX_IDENTIFIERS,
  '/traffic': FEATURES.TRAFFIC,
  '/transactionanalyzer': FEATURES.TRANSACTION_ANALYZER,
  '/waitlines': FEATURES.WAITLINES,
  '/zones': FEATURES.ZONES,
};

// Human-readable feature names for display
export const FEATURE_DISPLAY_NAMES = {
  [FEATURES.CCM]: 'Common Config Manager',
  [FEATURES.DCN_AUDIT]: 'DCN Audit',
  [FEATURES.GENERATE_PRICE_DCN]: 'Generate Price DCN',
  [FEATURES.GENERATE_PRODUCT_DCN]: 'Generate Product DCN',
  [FEATURES.O2O_CAPABILITIES]: 'O2O Capabilities',
  [FEATURES.O2O_ORDER_LOOKUP]: 'O2O Order Lookup',
  [FEATURES.PATCH]: 'Product Patch',
  [FEATURES.POS_REASONS]: 'POS Reasons',
  [FEATURES.PRINTER_PORTAL]: 'Printer Portal',
  [FEATURES.PRODUCT_ANALYZER]: 'Product Analyzer',
  [FEATURES.REGISTER_LEVEL_TRACKING]: 'Register Level Tracking',
  [FEATURES.SCS]: 'Store Config Service',
  [FEATURES.SCS_BULK_EDIT]: 'SCS Bulk Edit',
  [FEATURES.SCS_CONFIG_BY_KEY]: 'SCS Config By Key',
  [FEATURES.SCS_SCHEDULED_CHANGES]: 'SCS Scheduled Changes',
  [FEATURES.SLS]: 'Store Location Services',
  [FEATURES.SLS_DIGITAL_STORES]: 'SLS - Digital Stores',
  [FEATURES.SLS_STORES]: 'SLS - Stores',
  [FEATURES.SLS_WAREHOUSES]: 'SLS - Warehouses',
  [FEATURES.SNI_TRANSACTION_SEARCH]: 'SNI Transaction Search',
  [FEATURES.STORE_HIERARCHY]: 'Store Hierarchy',
  [FEATURES.STORE_HIERARCHY_SCHEDULE]: 'Store Hierarchy Schedule',
  [FEATURES.STORE_SERVICES]: 'Store Services',
  [FEATURES.TAX_IDENTIFIERS]: 'Tax Identifiers',
  [FEATURES.TRAFFIC]: 'Traffic',
  [FEATURES.TRANSACTION_ANALYZER]: 'Transaction Analyzer',
  [FEATURES.WAITLINES]: 'Waitlines',
  [FEATURES.ZONES]: 'Zones',
};

// SLS-specific event names (sorted alphabetically)
export const SLS_EVENTS = {
  API_CALL_ERROR: 'slsApiCallError',
  API_CALL_MADE: 'slsApiCallMade',
  API_CALL_SUCCESS: 'slsApiCallSuccess',
  API_LATENCY: 'slsApiLatency',
  BULK_CREATE_COMPLETED: 'slsBulkCreateCompleted',
  BULK_CREATE_STARTED: 'slsBulkCreateStarted',
  BULK_EDIT_COMPLETED: 'slsBulkEditCompleted',
  BULK_EDIT_STARTED: 'slsBulkEditStarted',
  DIGITAL_STORE_CREATED: 'slsDigitalStoreCreated',
  DIGITAL_STORE_UPDATED: 'slsDigitalStoreUpdated',
  EDIT_COMPLETED: 'slsEditCompleted',
  EDIT_DURATION: 'slsEditDuration',
  EDIT_STARTED: 'slsEditStarted',
  FACILITY_TYPE_SWITCHED: 'slsFacilityTypeSwitched',
  FORM_ABANDONED: 'slsFormAbandoned',
  FORM_ERROR_DISPLAYED: 'slsFormErrorDisplayed',
  FORM_SUBMITTED: 'slsFormSubmitted',
  MANDATORY_FIELD_MISSING: 'slsMandatoryFieldMissing',
  PAGE_VIEWED: 'slsPageViewed',
  REGION_FILTERED: 'slsRegionFiltered',
  REGULAR_HOURS_MODIFIED: 'slsRegularHoursModified',
  SEARCH_PERFORMED: 'slsSearchPerformed',
  SPECIAL_HOURS_ADDED: 'slsSpecialHoursAdded',
  SPECIAL_HOURS_REMOVED: 'slsSpecialHoursRemoved',
  STORE_CREATED: 'slsStoreCreated',
  STORE_FIELD_MODIFIED: 'slsStoreFieldModified',
  STORE_HOURS_UPDATED: 'slsStoreHoursUpdated',
  STORE_SELECTED: 'slsStoreSelected',
  STORE_UPDATED: 'slsStoreUpdated',
  UPDATE_CORRECTED: 'slsUpdateCorrected',
  UPDATE_FAILED: 'slsUpdateFailed',
  VALIDATION_ERROR: 'slsValidationError',
  WAREHOUSE_CREATED: 'slsWarehouseCreated',
  WAREHOUSE_UPDATED: 'slsWarehouseUpdated',
};

// SCS-specific event names (sorted alphabetically)
export const SCS_EVENTS = {
  BULK_EDIT_ERROR: 'scsBulkEditError',
  BULK_EDIT_EXECUTED: 'scsBulkEditExecuted',
  BULK_EDIT_FILTER_APPLIED: 'scsBulkEditFilterApplied',
  BULK_EDIT_SCOPE_SELECTED: 'scsBulkEditScopeSelected',
  BULK_EDIT_STARTED: 'scsBulkEditStarted',
  BULK_EDIT_SUCCESS: 'scsBulkEditSuccess',
  CONFIG_BY_KEY_DOWNLOADED: 'scsConfigByKeyDownloaded',
  CONFIG_BY_KEY_FILTER_APPLIED: 'scsConfigByKeyFilterApplied',
  CONFIG_BY_KEY_HISTORY_VIEWED: 'scsConfigByKeyHistoryViewed',
  CONFIG_BY_KEY_SEARCHED: 'scsConfigByKeySearched',
  CONFIG_FETCH_ERROR: 'scsConfigFetchError',
  CONFIG_FETCHED: 'scsConfigFetched',
  CONFIG_SAVE_ERROR: 'scsConfigSaveError',
  CONFIG_SAVED: 'scsConfigSaved',
  COUNTRY_SELECTED: 'scsCountrySelected',
  CREATE_KEY_OVERLAY_CLOSED: 'scsCreateKeyOverlayClosed',
  CREATE_KEY_OVERLAY_OPENED: 'scsCreateKeyOverlayOpened',
  KEY_CREATED: 'scsKeyCreated',
  KEY_CREATION_ERROR: 'scsKeyCreationError',
  KEY_EDIT_STARTED: 'scsKeyEditStarted',
  KEY_SCOPE_CHANGED: 'scsKeyScopeChanged',
  KEY_SEARCHED: 'scsKeySearched',
  PAGE_VIEWED: 'scsPageViewed',
  REGION_SELECTED: 'scsRegionSelected',
  SCOPE_DIFF_CHECK_BULK_UPDATE_ERROR: 'scsScopeDiffCheckBulkUpdateError',
  SCOPE_DIFF_CHECK_BULK_UPDATE_SUCCESS: 'scsScopeDiffCheckBulkUpdateSuccess',
  SCOPE_DIFF_CHECK_CANCELLED: 'scsScopeDiffCheckCancelled',
  SCOPE_DIFF_CHECK_DOWNLOADED: 'scsScopeDiffCheckDownloaded',
  SCOPE_DIFF_CHECK_TRIGGERED: 'scsScopeDiffCheckTriggered',
  STORE_SELECTED: 'scsStoreSelected',
};

// General analytics event names (sorted alphabetically)
export const ANALYTICS_EVENTS = {
  API_ERROR: 'apiError',
  FEATURE_ENGAGEMENT: 'featureEngagement',
  FEATURE_USED: 'featureUsed',
  PAGE_ENTERED: 'pageEntered',
  PAGE_EXITED: 'pageExited',
  SESSION_ENDED: 'sessionEnded',
  SESSION_HEARTBEAT: 'sessionHeartbeat',
  SESSION_STARTED: 'sessionStarted',
  TAB_VISIBILITY_CHANGED: 'tabVisibilityChanged',
  USER_ACTIVE: 'userActive',
  USER_IDLE: 'userIdle',
  USER_JOURNEY_STEP: 'userJourneyStep',
  VALIDATION_ERROR: 'validationError',
};

// Idle timeout in milliseconds (2 minutes)
export const IDLE_TIMEOUT_MS = 2 * 60 * 1000;

// Heartbeat interval in milliseconds (2 minutes - reduced frequency to prevent ERR_INSUFFICIENT_RESOURCES)
export const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

// Throttle interval for New Relic events in milliseconds (500ms minimum between events)
export const EVENT_THROTTLE_MS = 500;
